// OSPROD-145: circuit breaker for camera permission denials.
//
// When Chrome's per-site camera permission for our origin is set to "Block",
// every navigator.mediaDevices.getUserMedia({ video }) returns
// NotAllowedError: "Permission denied" immediately, with NO prompt. The
// browser will keep refusing until the user changes the site setting and
// (per Chrome's lifecycle) reloads. Without a circuit breaker, dropdown
// mounts, devicechange listeners, retry loops, and LiveKit's internal
// recovery paths can fire HUNDREDS of denied gUM calls in a single
// session — Ivan's 2026-05-28 webrtc dump showed **152 denied video gUM
// in ~6 minutes** (22 of them inside a 2-second burst at 15:34:11).
//
// This module:
//   1. Wraps gUM({ video }) — the FIRST NotAllowedError marks a sticky
//      flag and shows a single (cooldown-gated) Russian toast explaining
//      how to fix it. Subsequent calls short-circuit against a cached
//      error without hitting the browser.
//   2. Watches `navigator.permissions.query({ name: 'camera' })` for an
//      onchange event (Chrome, Firefox, modern Edge). When the user flips
//      the permission back to "allow" / "ask" in chrome://settings, the
//      flag auto-clears and a "restored" toast lets them know they can
//      now enable the camera — no page reload required.
//
// Wire-up: import `requestVideoStream` instead of calling
// `navigator.mediaDevices.getUserMedia({ video })` directly. mediaManager
// additionally consults `isCameraPermissionDenied()` before delegating to
// livekit-client's internal capture path, so toggling the camera button
// while the flag is set re-shows the toast without a browser round-trip.
//
// Audio gUM has its own dedicated path through mediaManager.applyDenoiserMic
// (raw → denoiser → publish) and is NOT routed through here. This module
// covers VIDEO ONLY.

import { useToastStore } from '../stores/toastStore'

let denied = false
let permissionWatcherInstalled = false
let lastToastAt = 0

// Don't spam: same toast at most once per minute. Subsequent denied gUMs
// short-circuit silently — the flag is sticky, the message stands.
const TOAST_COOLDOWN_MS = 60_000

function maybeShowDeniedToast(reason: 'gum' | 'toggle'): void {
  const now = Date.now()
  if (now - lastToastAt < TOAST_COOLDOWN_MS) return
  lastToastAt = now
  try {
    useToastStore.getState().showToast({
      id: 'camera-permission-blocked',
      type: 'error',
      title: 'Камера заблокирована',
      body:
        reason === 'toggle'
          ? 'Доступ к камере выключен в настройках сайта. Нажми значок замка слева от адреса → разреши камеру → перезагрузи страницу.'
          : 'Этот сайт не имеет доступа к камере. Нажми значок замка слева от адреса → Camera = Allow → перезагрузи страницу.',
    })
  } catch {
    // Toast store may not be initialised yet (e.g. SSR / earliest boot).
    // The console.warn in markCameraPermissionDenied below covers the
    // log side.
  }
}

function installPermissionWatcher(): void {
  if (permissionWatcherInstalled) return
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return
  permissionWatcherInstalled = true
  navigator.permissions
    .query({ name: 'camera' as PermissionName })
    .then((status) => {
      const apply = () => {
        if (status.state === 'granted' || status.state === 'prompt') {
          if (denied) {
            denied = false
            lastToastAt = 0
            try {
              useToastStore.getState().showToast({
                id: 'camera-permission-restored',
                type: 'message',
                title: 'Доступ к камере восстановлен',
                body: 'Теперь камеру можно включить.',
              })
            } catch { /* noop */ }
            console.info('[cameraPermission] permission flipped to', status.state, '— circuit-breaker cleared')
          }
        } else if (status.state === 'denied' && !denied) {
          // Permission was just blocked in chrome://settings — trip the
          // breaker proactively so the next gUM doesn't even try.
          denied = true
          console.warn('[cameraPermission] permission state observed as "denied" — circuit-breaker engaged')
        }
      }
      apply()
      // PermissionStatus events: 'change' on modern Chromium/Firefox.
      status.addEventListener?.('change', apply)
    })
    .catch(() => {
      // Older Safari: navigator.permissions.query with name:'camera' may
      // throw. Without the watcher, the breaker stays sticky until page
      // reload, which is still strictly better than the retry storm.
    })
}

export function isCameraPermissionDenied(): boolean {
  return denied
}

/**
 * Engage the sticky circuit breaker. Idempotent; the second call within
 * the cooldown window will NOT re-show the toast (avoids spam during a
 * burst of denied callbacks before the breaker takes effect upstream).
 */
export function markCameraPermissionDenied(reason: 'gum' | 'toggle' = 'gum'): void {
  if (!denied) {
    denied = true
    console.warn('[cameraPermission] camera permission denied — circuit-breaker engaged (reason:', reason, ')')
    installPermissionWatcher()
  }
  maybeShowDeniedToast(reason)
}

/**
 * Force-clear the breaker. The Permissions API watcher handles this
 * automatically when supported; this is the manual escape hatch for
 * test code and explicit "I fixed it" actions.
 */
export function clearCameraPermissionDenied(): void {
  denied = false
  lastToastAt = 0
}

/**
 * Drop-in replacement for `navigator.mediaDevices.getUserMedia({ video })`.
 *
 * Use at every UI call site that requests the camera. Routes through the
 * sticky circuit-breaker so a permission denial trips ONE browser call and
 * stops — no retry storms. Other gUM errors (NotFoundError, NotReadableError,
 * OverconstrainedError, etc.) pass through unchanged — they may still need
 * call-site-specific handling, but they don't trigger the same denial-spiral
 * the Block-set-permission did.
 */
export async function requestVideoStream(
  constraints: MediaStreamConstraints,
): Promise<MediaStream> {
  if (denied) {
    maybeShowDeniedToast('gum')
    const err = new Error('Permission denied (cached by circuit breaker)') as Error & { name: string }
    err.name = 'NotAllowedError'
    throw err
  }
  installPermissionWatcher()
  try {
    return await navigator.mediaDevices.getUserMedia(constraints)
  } catch (err) {
    const name = (err as { name?: string } | null)?.name
    if (name === 'NotAllowedError') {
      markCameraPermissionDenied('gum')
    }
    // AbortError (user clicked Cancel on the OS prompt) is NOT terminal —
    // they might allow on the next try. Don't trip the breaker for it.
    throw err
  }
}
